import React, { useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';

import { RoleApi } from '../../services/RoleApi';
import { PermissionApi } from '../../services/PermissionApi';

import { Modal, Grid, useTheme, useMediaQuery } from '@mui/material';
import { AddCircleOutlineRounded, SaveAltRounded } from '@mui/icons-material';

import TableRoles from '../../components/atoms/tables/TableRoles';
import { BackdropLoading } from '../../components/BackdropLoading';
import { ShowDialog } from '../../components/ShowDialog';
import { RoleModalCard } from '../../components/atoms/modalCards/RoleModalCard';
import { ListPermissions } from '../../components/atoms/modalCards/ListPermissions'
import { ChangePermissions } from '../../components/atoms/modalCards/ChangePermissions'
import { SearchBarRoles } from '../../components/atoms/searchBars/SearchBarRoles';
import { ColorsContext } from "../../Context/ColorsContext"  

import { 
    Container,
    CustomButtonV2,
	PageTitle,
 } from '../styles';

import {
  BoxModal,
} from './styles';

import { useTranslation } from 'react-i18next'
//?? Utils
import { FormatDate } from '../../utils/formatDate'
//?? Custom Hook
import { useDownloadExcel } from '../../Hooks/useDownloadExcel';
import { RelationRoles } from '../../components/atoms/modalCards/RelationRoles';

// Permissions
import { useAbility } from '@casl/react';
import { AbilityContext } from '../../Context/PermissionsContext'

export const PermissionList = (props) => {
    const { t } = useTranslation();
    const { colors } = React.useContext(ColorsContext);
    const ability = useAbility(AbilityContext);
	const theme = useTheme();
	const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

    const roleApi = new RoleApi();
    const permissionApi = new PermissionApi();

    const { format } = new FormatDate();

    const refData = React.useRef(false)
    const actionRef = React.useRef('CREATE')
    const roleIdRef = React.useRef(0)
    const [data, setData] = React.useState([]);
    const [filteredRequest, setFilteredRequest] = React.useState([])
    const [title, setTitle] = React.useState(t('system.permissions.role'))

    const [loading, setLoading] = React.useState(false);
    const [dialog, setDialog] = React.useState({
        title: '',
        message: '',
        status: false
    });

    const [openRoleForm, setOpenRoleForm] = React.useState(false)
    const [openListForm, setOpenListForm] = React.useState(false)
    const [openChangePermissionForm, setOpenChangePermissionForm] = React.useState(false)
    const [openRelateRoleForm, setOpenRelateRoleForm] = React.useState(false)

    const handleCloseModalList = () => setOpenListForm(false);
    const handleCloseModalChangePermission = () => setOpenChangePermissionForm(false);
    const handleCloseModalRelateRole = () => setOpenRelateRoleForm(false);

    const [form, setForm] = React.useState({
        id: 0,
        name: '',
        description: '',
        isActive: false,
        isExternal: false,
        isRestricted: false
    })

    const [errors, setErrors] = React.useState({
        name: { hasError: false, message: '' },
        description: { hasError: false, message: '' },
        general: { hasError: false, message: '' }
    })

    //?? prepare fields-table for download Excel
    const fieldConfig = {
        [t('system.permissions.table.id')]: 'id',
        [t('system.permissions.table.names')]: 'name',
        [t('system.permissions.table.description')]: 'description',
        [t('system.permissions.table.status')]: 'isEnabledStr',
        [t('system.permissions.table.createdDate')]: (item) => format({ date: item.createdAt, format: true }).toString(),
    };

    //?? Custom Hook Download Excel 
    const { handleDownloadExcelList } = useDownloadExcel(filteredRequest, fieldConfig, t('system.permissions.title'));

    const handleChange = (event, key) => {
        let e = event.target
        let value = e.type === 'checkbox' ? e.checked : e.value
        
        if (key === 'isExternal' && value) {
            setForm({ ...form, isExternal: true, isRestricted: true })
        }
        else {
            setForm({ ...form, [`${key}`]: value })
        }
    }

    const handleOpen = () => {
        actionRef.current = 'CREATE'
        resetForm()
        resetErrors()
        setOpenRoleForm(true)
    }
    
    const handleCloseModal = () => {
        resetErrors()
        setOpenRoleForm(false)
    }

    const handleToggle = () => {
        resetErrors()
        setLoading(true)
        const data = {
            ...form,
            isActive: !form.isActive
        }
        roleApi.toggleState(data.id, data).then((resp) => {
            if (resp?.status === 204) {
                resetForm()
                getRoles()
            } else {
                toast.error(() => 
                    <div>{t('system.permissions.messages.updatedData.error')}<br/>
                        {resp?.data?.description || resp?.data?.data?.description || resp?.response?.data?.description || resp?.response?.data?.data?.description || t('system.errorSaving')}
                    </div>
                );
            }
            setLoading(false)
        })
    }

    const handleEdit = () => {
        setOpenRoleForm(true)
    }

    const handleView = () => {
        setTitle(t('system.permissions.permissionsList'))
        setOpenListForm(true)
    }

    const handleAssign = () => {
        setOpenChangePermissionForm(true)
    }
    
    const handleRelate = () => {
        setOpenRelateRoleForm(true)
    }

    const validateForm = () => {
        let listErrors = { ...errors }
        let hasError = false
    
        if (form.name === '') {
          listErrors.name = { hasError: true, message: t('system.validations.mandatory') }
          hasError = true
        }
        if (form.description === '') {
          listErrors.description = { hasError: true, message: t('system.validations.mandatory') }
          hasError = true
        }
    
        setErrors(listErrors)
    
        return hasError
      }

    function handleCloseDialog(result) {
        setDialog({...dialog, status: false})

        if (result) {
            if (actionRef.current === 'CREATE' || actionRef.current === 'UPDATE') handleAccept()
            if (actionRef.current === 'DEACTIVATE' || actionRef.current === 'ACTIVATE') handleToggle()
        }
    }

    function handleChangePermissions(id, data) {
        permissionApi.updatePermissions(id, data).then((resp) => {
            setLoading(false)
            if (resp?.status === 204) {
                resetErrors()
                setOpenChangePermissionForm(false)
                setDialog({
                    title: t('system.permissions.messages.updatedData.title'), 
                    message: t('system.permissions.messages.updatedData.message'),
                    status: true
                })
            } else {
                toast.error(() => 
                    <div>{t('system.permissions.messages.updatedData.error')}<br/>
                        {resp?.data?.description || resp?.data?.data?.description || resp?.response?.data?.description || resp?.response?.data?.data?.description || t('system.errorSaving')}
                    </div>
                );
            }
        })
    }

    function handleChangeRelateRoles(id, data) {
        roleApi.updateRelation(id, data).then((resp) => {
            setLoading(false)
            if (resp?.status === 204) {
                resetErrors()
                setOpenRelateRoleForm(false)
                setDialog({
                    title: t('system.permissions.messages.updatedData.title'), 
                    message: t('system.permissions.messages.updatedData.message'),
                    status: true
                })
            } else {
                toast.error(() => 
                    <div>{t('system.permissions.messages.updatedData.error')}<br/>
                        {resp?.data?.description || resp?.data?.data?.description || resp?.response?.data?.description || resp?.response?.data?.data?.description || t('system.errorSaving')}
                    </div>
                );
            }
        })
    }

    const handleAccept = () => {
        if (!validateForm()) {
            setLoading(true)
            if (actionRef.current === 'CREATE') {
                roleApi.create(form).then((resp) => {
                    setLoading(false)
                    if(resp?.status === 200) {
                        getRoles()
                        resetForm()
                        handleCloseModal()
                        setDialog({
                            title: t('system.permissions.messages.saveData.title'), 
                            message: t('system.permissions.messages.saveData.message'),
                            status: true
                        })
                    } else {
                        toast.error(() => 
                            <div>{t('system.permissions.messages.saveData.error')}<br/>
                                {resp?.data?.description || resp?.data?.data?.description || resp?.response?.data?.description || resp?.response?.data?.data?.description || t('system.errorSaving')}
                            </div>
                        );
                    }
                })
            } else {
                roleApi.update(form.id, form).then((resp) => {
                    setLoading(false)
                    if (resp?.status === 204) {
                        getRoles()
                        resetForm()
                        handleCloseModal()
                        setDialog({
                            title: t('system.permissions.messages.updatedData.title'), 
                            message: t('system.permissions.messages.updatedData.message'),
                            status: true
                        })
                    } else {
                        toast.error(() => 
                            <div>{t('system.permissions.messages.updatedData.error')}<br/>
                                {resp?.data?.description || resp?.data?.data?.description || resp?.response?.data?.description || resp?.response?.data?.data?.description || t('system.errorSaving')}
                            </div>
                        );
                    }
                })
            }
        }
    }

    const resetForm = () => {
        setForm({
            id: 0,
            name: '',
            description: '',
            isActive: false,
            isExternal: false,
            isRestricted: false
        })
    }

    const resetErrors = () => {
        setErrors({
            name: { hasError: false, message: '' },
            description: { hasError: false, message: '' },
            general: { hasError: false, message: '' }
        })
    }

    const selectedRole = (item, type) => {
        actionRef.current = type
        roleIdRef.current = item.id
        setForm({
            ...form,
            id: item.id,
            name: item.name,
            description: item.description,
            isActive: item.isActive,
            isExternal: item.isExternal || false,
            isRestricted: item.isRestricted || false,
        })

        if (actionRef.current === 'UPDATE') handleEdit()
        if (actionRef.current === 'DEACTIVATE' || actionRef.current === 'ACTIVATE') handleToggleRole(actionRef.current, item.name)
        if (actionRef.current === 'VIEW') handleView()
        if (actionRef.current === 'ASSIGN') handleAssign()
        if (actionRef.current === 'RELATE') handleRelate()
    }

    function handleToggleRole(state, roleName) {
        setDialog({
            status: true,
            title: t('system.permissions.role'),
            message: t('system.permissions.confirmChangeRole', { state: state === 'DEACTIVATE' ? 'system.permissions.disable' : 'system.permissions.enable', role: roleName}),
            type: 'confirmation'
        })
    }

    const getRoles = useCallback(async() => {
        let response = await roleApi.list();
        if (response.status === 200) {
            let resp = response.data.data;
            setFilteredRequest(resp);
            setData(resp)
        }
    }, [roleApi])

    useEffect(() => {
        if (refData.current === false) {
          getRoles()
          refData.current = true
        }
    }, [getRoles])

    return (
        <>
            <Container open={props.open} style={{ marginBottom: 0 }}>
                <BackdropLoading open={loading}/>
                <ShowDialog openDialog={dialog.status} dialog={dialog} handleCloseDialog={handleCloseDialog}/>

                <Grid container 
					spacing={1}
					alignItems="center"
					sx={{ mb: { md: 2 } }}
					columns={{ xs: 12, sm: 12, md: 12, lg: 23, xl: 15 }}>
                    <Grid item xs={12} sm={12} md={12} lg={6} xl={4}>
                        <PageTitle>{t('system.permissions.title')} </PageTitle>
                    </Grid>

                    <Grid item xs={12} sm={6} md={6} lg={8} xl={6}>
						<SearchBarRoles rows={data} setFilteredRequest={setFilteredRequest} />
					</Grid>

					{isDesktop &&
						<Grid item md={false} lg={1} xl={2}></Grid>
					}

					<Grid item xs={12} sm={6} md={6} lg={8} xl={3}
            			sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
						{ability.can("CreateRole") && (
							<CustomButtonV2
								variant="contained"
								startIcon={<AddCircleOutlineRounded/>}
								aria-label="add-user"
								onClick={handleOpen}
								btnType="primary"
							>
								{t('system.permissions.createRol')}
							</CustomButtonV2>
						)}

						<CustomButtonV2
							variant="contained"
							startIcon={<SaveAltRounded />}
							aria-label="download"
							onClick={handleDownloadExcelList}
						>
							{t('common.download')}
						</CustomButtonV2>
					</Grid>
                </Grid>

                <TableRoles
                    filteredRequest={filteredRequest}
                    rows={data}
                    selectedRole={selectedRole}
                />
            </Container>
            <Modal open={openRoleForm}>
                <BoxModal>
                    <RoleModalCard 
                        title={actionRef.current === 'CREATE' ? t('system.permissions.roleCreation') : t('system.permissions.roleUpdate')}
                        loading={loading}
                        data={form}
                        errors={errors}
                        handleClose={handleCloseModal}
                        handleChange={handleChange}
                        handleAccept={handleAccept}
                    />
                </BoxModal>
            </Modal>
            <Modal open={openListForm}>
                <BoxModal>
                    <ListPermissions
                        roleId={roleIdRef.current}
                        title={title}
                        handleClose={handleCloseModalList}
                    />
                </BoxModal>
            </Modal>
            <Modal open={openChangePermissionForm}>
                <BoxModal>
                    <ChangePermissions
                        roleId={roleIdRef.current}
                        title={`${t('system.permissions.editRolePermissions')} ${form.name.toUpperCase()}`}
                        loading={loading}
                        handleClose={handleCloseModalChangePermission}
                        handleChangePermissions={handleChangePermissions}
                    />
                </BoxModal>
            </Modal>
            <Modal open={openRelateRoleForm}>
                <BoxModal>
                    <RelationRoles
                        roleId={roleIdRef.current}
                        title={`${t('system.permissions.editRelationRole')} ${form.name.toUpperCase()}`}
                        loading={loading}
                        handleClose={handleCloseModalRelateRole}
                        handleChangeRelateRoles={handleChangeRelateRoles}
                    />
                </BoxModal>
            </Modal>
        </>
    )
}